import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Menu, X, LogOut } from "lucide-react";

const Brand = () => (
  <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
    <span className="relative inline-flex h-8 w-8 items-center justify-center">
      <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#e6c687] to-[#d4af37] opacity-90"></span>
      <span className="absolute inset-[3px] rounded-full bg-[#0a0a0c]"></span>
      <span className="relative font-serif text-[#e6c687] text-sm">P</span>
    </span>
    <span className="font-serif text-[15px] tracking-wide text-[#f8f8fc] group-hover:text-[#e6c687] transition-colors">
      PERSONA <span className="text-[#e6c687]">FRAGRANCE INTELLIGENCE</span>
    </span>
  </Link>
);

export default function Header({ transparent = false }) {
  const { user, logout } = useAuth();
  const { tr } = useLang();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const items = user
    ? [
        { to: "/dashboard", label: tr("nav_dashboard"), id: "nav-dashboard" },
        { to: "/ai-perfumer", label: "PERSONA AI PERFUMER", id: "nav-ai-perfumer", untranslated: true },
        { to: "/research", label: tr("p5_nav_research"), id: "nav-research" },
        { to: "/ptc-wallet", label: tr("p5_nav_ptc"), id: "nav-ptc" },
        { to: "/profile", label: tr("nav_profile"), id: "nav-profile" },
        { to: "/history", label: tr("nav_history"), id: "nav-history" },
        { to: "/academy", label: "IPTECHS ACADEMY", id: "nav-academy", untranslated: true },
        { to: "/subscription", label: tr("nav_subscription"), id: "nav-subscription" },
        { to: "/perfumery-tech", label: "PERSONA PERFUMERY TECH", id: "nav-tech", untranslated: true },
        { to: "/account", label: tr("nav_my_account"), id: "nav-account" },
      ]
    : [
        { to: "/journey", label: tr("nav_journey"), id: "nav-journey" },
      ];

  return (
    <header
      data-testid="site-header"
      className={
        "sticky top-0 z-40 w-full border-b border-white/5 " +
        (transparent ? "bg-[#0a0a0c]/60" : "bg-[#0a0a0c]/85") +
        " backdrop-blur-xl"
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8 min-w-0">
          <Brand />
          {user ? (
            <nav className="hidden lg:flex items-center gap-6">
              {items.map((i) => (
                <NavLink
                  key={i.id}
                  to={i.to}
                  data-testid={i.id}
                  className={({ isActive }) =>
                    "text-[12.5px] tracking-wide transition-colors " +
                    (isActive
                      ? "text-[#e6c687]"
                      : "text-[#a9a9b8] hover:text-[#f8f8fc]")
                  }
                >
                  {i.label}
                </NavLink>
              ))}
            </nav>
          ) : (
            <nav className="hidden md:flex items-center gap-6">
              {items.map((i) => (
                <NavLink
                  key={i.id}
                  to={i.to}
                  data-testid={i.id}
                  className={({ isActive }) =>
                    "text-[12.5px] tracking-wide transition-colors " +
                    (isActive
                      ? "text-[#e6c687]"
                      : "text-[#a9a9b8] hover:text-[#f8f8fc]")
                  }
                >
                  {i.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {!user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/login"
                data-testid="header-signin-btn"
                className="px-4 py-2 text-sm text-[#e6c687] hover:text-[#f8f8fc] transition-colors"
              >
                {tr("nav_signin")}
              </Link>
              <Link
                to="/register"
                data-testid="header-signup-btn"
                className="pfi-btn-gold px-4 py-2 rounded-full text-sm"
              >
                {tr("nav_signup")}
              </Link>
            </div>
          ) : (
            <button
              onClick={() => { logout(); nav("/"); }}
              data-testid="header-signout-btn"
              className="pfi-btn-outline px-3 py-2 rounded-full text-xs inline-flex items-center gap-2"
            >
              <LogOut size={14} /> {tr("nav_signout")}
            </button>
          )}
          {user && (
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-btn"
              className="lg:hidden text-[#e6c687] p-2"
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
        </div>
      </div>

      {user && open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0a0a0c] px-4 py-3 space-y-2">
          {items.map((i) => (
            <NavLink
              key={i.id}
              to={i.to}
              data-testid={`m-${i.id}`}
              onClick={() => setOpen(false)}
              className="block py-2 text-sm text-[#a9a9b8] hover:text-[#e6c687]"
            >
              {i.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
